import React, { useState } from 'react'
import { useAuth } from '../../context/AuthContext'
import { useAppData } from '../../context/AppContext'
import { dataService } from '../../services/dataService'
import { PartnerRateSheet, PartnerRateSheetEntry } from '../../types'
import { Modal } from '../ui/Modal'
import { Badge } from '../ui/Badge'
import { Plus, EditIcon, Trash2 } from '../icons/Icons'
import s from './shared.module.css'

export default function PartnerRateSheets() {
  const { organization } = useAuth()
  const { partners, partnerRateSheets, partnerRateSheetEntries, resourceRoleTypes, refreshData } = useAppData()
  const orgId = organization!.id

  const [showSheetModal, setShowSheetModal] = useState(false)
  const [showEntryModal, setShowEntryModal] = useState(false)
  const [editingSheet, setEditingSheet] = useState<PartnerRateSheet | null>(null)
  const [editingEntry, setEditingEntry] = useState<PartnerRateSheetEntry | null>(null)
  const [selectedSheetId, setSelectedSheetId] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)
  const [sheetError, setSheetError] = useState('')
  const [entryError, setEntryError] = useState('')

  const [sheetForm, setSheetForm] = useState({ title: '', partner_id: '', description: '', active: true })
  const [entryForm, setEntryForm] = useState({ partner_rate_sheet_id: '', resource_role_type_id: '', billing_rate: 0, cost_rate: 0, currency: 'USD' as any })

  const openAddSheet = () => { setEditingSheet(null); setSheetForm({ title: '', partner_id: '', description: '', active: true }); setSheetError(''); setShowSheetModal(true) }
  const openEditSheet = (s2: PartnerRateSheet) => { setEditingSheet(s2); setSheetForm({ title: s2.title, partner_id: s2.partner_id, description: s2.description || '', active: s2.active }); setSheetError(''); setShowSheetModal(true) }
  const openAddEntry = (sheetId: string) => { setEditingEntry(null); setEntryForm({ partner_rate_sheet_id: sheetId, resource_role_type_id: '', billing_rate: 0, cost_rate: 0, currency: 'USD' }); setEntryError(''); setShowEntryModal(true) }
  const openEditEntry = (e2: PartnerRateSheetEntry) => { setEditingEntry(e2); setEntryForm({ partner_rate_sheet_id: e2.partner_rate_sheet_id, resource_role_type_id: e2.resource_role_type_id, billing_rate: e2.billing_rate, cost_rate: e2.cost_rate, currency: e2.currency }); setEntryError(''); setShowEntryModal(true) }

  const handleSheetSubmit = async (e: React.FormEvent) => {
    e.preventDefault(); if (saving) return; setSaving(true); setSheetError('')
    try {
      if (editingSheet) await dataService.updatePartnerRateSheet(editingSheet.id, sheetForm)
      else await dataService.createPartnerRateSheet(orgId, sheetForm as any)
      setShowSheetModal(false); await refreshData()
    } catch (err: any) { setSheetError(err?.message || 'Failed to save. Please try again.') } finally { setSaving(false) }
  }

  const handleEntrySubmit = async (e: React.FormEvent) => {
    e.preventDefault(); if (saving) return; setSaving(true); setEntryError('')
    try {
      if (editingEntry) await dataService.updatePartnerRateSheetEntry(editingEntry.id, entryForm)
      else await dataService.createPartnerRateSheetEntry(orgId, entryForm as any)
      setShowEntryModal(false); await refreshData()
    } catch (err: any) { setEntryError(err?.message || 'Failed to save. Please try again.') } finally { setSaving(false) }
  }

  const handleDeleteSheet = async (id: string) => {
    if (!confirm('Delete this rate sheet?')) return
    await dataService.deletePartnerRateSheet(id); await refreshData()
  }

  const handleDeleteEntry = async (id: string) => {
    if (!confirm('Delete this rate entry?')) return
    await dataService.deletePartnerRateSheetEntry(id); await refreshData()
  }

  const getPartnerName = (id: string) => partners.find(p => p.id === id)?.title || '—'
  const getRoleName = (id: string) => resourceRoleTypes.find(r => r.id === id)?.title || '—'

  const entriesForSheet = (sheetId: string) => partnerRateSheetEntries.filter(e => e.partner_rate_sheet_id === sheetId)

  return (
    <div>
      <div className={s.pageHeader}>
        <div><h2 className={s.pageTitle}>Partner Rate Sheets</h2><p className={s.pageSubtitle}>{partnerRateSheets.length} rate sheet{partnerRateSheets.length !== 1 ? 's' : ''}</p></div>
        <button className={s.primaryBtn} onClick={openAddSheet} data-tour-prs="add-sheet"><Plus size={14} /> Add Rate Sheet</button>
      </div>

      {partnerRateSheets.map(sheet => (
        <div key={sheet.id} className={s.card} style={{ marginBottom: 16 }}>
          <div className={s.cardHeader}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <h3 className={s.cardTitle}>{sheet.title}</h3>
              <span style={{ fontSize: 12, color: '#605e5c' }}>{getPartnerName(sheet.partner_id)}</span>
              <Badge variant={sheet.active ? 'success' : 'neutral'}>{sheet.active ? 'Active' : 'Inactive'}</Badge>
            </div>
            <div className={s.actions}>
              <button className={s.primaryBtn} style={{ fontSize: 12, padding: '4px 10px' }} onClick={() => openAddEntry(sheet.id)} data-tour-prs="add-rate"><Plus size={12} /> Add Rate</button>
              <button className={`${s.actionBtn} ${s.edit}`} onClick={() => openEditSheet(sheet)}><EditIcon size={14} /></button>
              <button className={`${s.actionBtn} ${s.del}`} onClick={() => handleDeleteSheet(sheet.id)}><Trash2 size={14} /></button>
            </div>
          </div>
          <table className={s.table}>
            <thead><tr><th>Role Type</th><th>Billing Rate</th><th>Cost Rate</th><th>Currency</th><th>Actions</th></tr></thead>
            <tbody>
              {entriesForSheet(sheet.id).length === 0 && <tr><td colSpan={5} className={s.emptyState}>No rates defined yet</td></tr>}
              {entriesForSheet(sheet.id).map(entry => (
                <tr key={entry.id}>
                  <td>{getRoleName(entry.resource_role_type_id)}</td>
                  <td>{entry.billing_rate}</td>
                  <td>{entry.cost_rate}</td>
                  <td>{entry.currency}</td>
                  <td><div className={s.actions}>
                    <button className={`${s.actionBtn} ${s.edit}`} onClick={() => openEditEntry(entry)}><EditIcon size={14} /></button>
                    <button className={`${s.actionBtn} ${s.del}`} onClick={() => handleDeleteEntry(entry.id)}><Trash2 size={14} /></button>
                  </div></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ))}

      {partnerRateSheets.length === 0 && <div className={s.card}><div className={s.emptyState}>No partner rate sheets yet</div></div>}

      <Modal open={showSheetModal} title={editingSheet ? 'Edit Rate Sheet' : 'Add Rate Sheet'} onClose={() => setShowSheetModal(false)}
        footer={<><button className={s.secondaryBtn} onClick={() => setShowSheetModal(false)}>Cancel</button><button className={s.primaryBtn} form="sheetForm" type="submit" disabled={saving}>{saving ? 'Saving...' : 'Save'}</button></>}>
        <form id="sheetForm" onSubmit={handleSheetSubmit}>
          {sheetError && <div className={s.formError}>{sheetError}</div>}
          <div className={s.formGroup}><label>Rate Sheet Name *</label><input value={sheetForm.title} onChange={e => setSheetForm(f => ({ ...f, title: e.target.value }))} required /></div>
          <div className={s.formGroup}><label>Partner *</label>
            <select value={sheetForm.partner_id} onChange={e => setSheetForm(f => ({ ...f, partner_id: e.target.value }))} required>
              <option value="">Select partner...</option>
              {partners.map(p => <option key={p.id} value={p.id}>{p.title}</option>)}
            </select>
          </div>
          <div className={s.formGroup}><label>Description</label><textarea value={sheetForm.description} onChange={e => setSheetForm(f => ({ ...f, description: e.target.value }))} /></div>
          <div className={s.checkboxGroup}><input type="checkbox" id="shActive" checked={sheetForm.active} onChange={e => setSheetForm(f => ({ ...f, active: e.target.checked }))} /><label htmlFor="shActive">Active</label></div>
        </form>
      </Modal>

      <Modal open={showEntryModal} title={editingEntry ? 'Edit Rate Entry' : 'Add Rate Entry'} onClose={() => setShowEntryModal(false)}
        footer={<><button className={s.secondaryBtn} onClick={() => setShowEntryModal(false)}>Cancel</button><button className={s.primaryBtn} form="entryForm" type="submit" disabled={saving}>{saving ? 'Saving...' : 'Save'}</button></>}>
        <form id="entryForm" onSubmit={handleEntrySubmit}>
          {entryError && <div className={s.formError}>{entryError}</div>}
          <div className={s.formGroup}><label>Role Type *</label>
            <select value={entryForm.resource_role_type_id} onChange={e => setEntryForm(f => ({ ...f, resource_role_type_id: e.target.value }))} required>
              <option value="">Select role...</option>
              {resourceRoleTypes.map(r => <option key={r.id} value={r.id}>{r.title}</option>)}
            </select>
          </div>
          <div className={s.formRow}>
            <div className={s.formGroup}><label>Billing Rate *</label><input type="number" step="0.01" min="0" value={entryForm.billing_rate} onChange={e => setEntryForm(f => ({ ...f, billing_rate: parseFloat(e.target.value) || 0 }))} required /></div>
            <div className={s.formGroup}><label>Cost Rate *</label><input type="number" step="0.01" min="0" value={entryForm.cost_rate} onChange={e => setEntryForm(f => ({ ...f, cost_rate: parseFloat(e.target.value) || 0 }))} required /></div>
          </div>
          <div className={s.formGroup}><label>Currency</label>
            <select value={entryForm.currency} onChange={e => setEntryForm(f => ({ ...f, currency: e.target.value as any }))}>
              <option value="USD">USD</option><option value="GBP">GBP</option><option value="INR">INR</option>
            </select>
          </div>
        </form>
      </Modal>
    </div>
  )
}
